import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIAgentsTab = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeAgent, setActiveAgent] = useState('forecast');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [assistantQuery, setAssistantQuery] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const agents = [
    { id: 'forecast', name: 'Forecast de Ventas', icon: '📈', description: 'Proyección de ventas con AI', roles: ['master_admin', 'admin'] },
    { id: 'pnl', name: 'Análisis P&L', icon: '💰', description: 'Estado de resultados con AI', roles: ['master_admin', 'admin'] },
    { id: 'menu-schedule', name: 'Programación Menú', icon: '📅', description: 'Menú mensual automático', roles: ['master_admin', 'admin'] },
    { id: 'purchase', name: 'Sugerencias Compras', icon: '🛒', description: 'Órdenes de compra AI', roles: ['master_admin', 'admin', 'compras'] },
    { id: 'production', name: 'Plan Producción', icon: '🏭', description: 'Plan diario de producción', roles: ['master_admin', 'admin', 'produccion'] },
    { id: 'routes', name: 'Rutas Entrega', icon: '🚚', description: 'Optimización de rutas', roles: ['master_admin', 'admin', 'produccion'] },
    { id: 'inventory', name: 'Alertas Inventario', icon: '📦', description: 'Monitoreo de stock', roles: ['master_admin', 'admin', 'compras', 'produccion'] },
    { id: 'assistant', name: 'Asistente AI', icon: '🤖', description: 'Pregunta lo que quieras', roles: ['all'] }
  ];

  const visibleAgents = agents.filter(a => 
    a.roles.includes('all') || a.roles.includes(user?.role)
  );

  const runAgent = async (agentId, customBody = {}) => {
    setLoading(true);
    setError('');
    setResult(null);

    const endpoints = {
      'forecast': '/api/ai/admin/forecast',
      'pnl': '/api/ai/admin/pnl',
      'menu-schedule': '/api/ai/admin/menu-schedule',
      'purchase': '/api/ai/ops/purchase-suggestions',
      'production': '/api/ai/ops/production-plan',
      'routes': '/api/ai/ops/delivery-routes',
      'inventory': '/api/ai/ops/inventory-alerts',
      'assistant': '/api/ai/assistant'
    };

    const defaultBodies = {
      'forecast': { period: 'month' },
      'pnl': {},
      'menu-schedule': { month: new Date().getMonth() + 1, year: new Date().getFullYear() },
      'purchase': {},
      'production': { date: new Date().toISOString().split('T')[0] },
      'routes': { date: new Date().toISOString().split('T')[0] },
      'inventory': {},
      'assistant': { query: assistantQuery, context: 'general' }
    };

    try {
      const res = await fetch(`${API_URL}${endpoints[agentId]}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...defaultBodies[agentId], ...customBody })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Error al ejecutar agente');
      }

      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatAIResponse = (response) => {
    if (!response) return null;
    
    // Try to parse as JSON
    try {
      const jsonMatch = response.match(/```json\n?([\s\S]*?)\n?```/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[1]);
        return (
          <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-auto text-sm max-h-96">
            {JSON.stringify(parsed, null, 2)}
          </pre>
        );
      }
    } catch (e) {}

    // Return as markdown-ish text
    return (
      <div className="prose prose-sm max-w-none">
        <pre className="whitespace-pre-wrap bg-gray-50 p-4 rounded-lg text-gray-800 text-sm">
          {response}
        </pre>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="ai-agents-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">🤖 AI Agents</h2>
          <p className="text-gray-500">Automatización inteligente para tu negocio</p>
        </div>
      </div>

      {/* Agent Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {visibleAgents.map(agent => (
          <button
            key={agent.id}
            onClick={() => setActiveAgent(agent.id)}
            className={`p-4 rounded-xl border-2 text-left transition-all ${
              activeAgent === agent.id
                ? 'border-emerald-500 bg-emerald-50'
                : 'border-gray-200 hover:border-emerald-300 bg-white'
            }`}
            data-testid={`agent-${agent.id}`}
          >
            <span className="text-3xl">{agent.icon}</span>
            <h3 className="font-semibold text-gray-800 mt-2">{agent.name}</h3>
            <p className="text-xs text-gray-500 mt-1">{agent.description}</p>
          </button>
        ))}
      </div>

      {/* Active Agent Panel */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-800">
            {agents.find(a => a.id === activeAgent)?.icon}{' '}
            {agents.find(a => a.id === activeAgent)?.name}
          </h3>
          {activeAgent !== 'assistant' && (
            <button
              onClick={() => runAgent(activeAgent)}
              disabled={loading}
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
              data-testid="run-agent-button"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Procesando...
                </>
              ) : (
                <>
                  ▶️ Ejecutar
                </>
              )}
            </button>
          )}
        </div>

        {/* Assistant Input */}
        {activeAgent === 'assistant' && (
          <div className="mb-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={assistantQuery}
                onChange={(e) => setAssistantQuery(e.target.value)}
                placeholder="Pregunta lo que necesites..."
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                onKeyPress={(e) => e.key === 'Enter' && assistantQuery && runAgent('assistant')}
                data-testid="assistant-input"
              />
              <button
                onClick={() => runAgent('assistant')}
                disabled={loading || !assistantQuery}
                className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                data-testid="assistant-submit"
              >
                {loading ? '...' : 'Enviar'}
              </button>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-100 text-red-700 rounded-lg mb-4" data-testid="agent-error">
            {error}
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="space-y-4" data-testid="agent-result">
            {/* Historical/Summary Data */}
            {result.historical_data && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600">Total Órdenes</p>
                  <p className="text-2xl font-bold text-blue-800">{result.historical_data.total_orders}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-green-600">Revenue Total</p>
                  <p className="text-2xl font-bold text-green-800">
                    ${result.historical_data.total_revenue?.toLocaleString('es-MX', { maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm text-purple-600">Ticket Promedio</p>
                  <p className="text-2xl font-bold text-purple-800">
                    ${result.historical_data.avg_order_value?.toFixed(2)}
                  </p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <p className="text-sm text-orange-600">Top Producto</p>
                  <p className="text-lg font-bold text-orange-800">
                    {result.historical_data.top_products?.[0]?.[0] || 'N/A'}
                  </p>
                </div>
              </div>
            )}

            {/* P&L Data */}
            {result.pnl && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600">Ingresos</p>
                  <p className="text-2xl font-bold text-blue-800">
                    ${result.pnl.revenue?.toLocaleString('es-MX', { maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-sm text-red-600">Costo de Ventas</p>
                  <p className="text-2xl font-bold text-red-800">
                    ${result.pnl.cogs?.toLocaleString('es-MX', { maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-green-600">Utilidad Bruta</p>
                  <p className="text-2xl font-bold text-green-800">
                    ${result.pnl.gross_profit?.toLocaleString('es-MX', { maximumFractionDigits: 0 })}
                  </p>
                  <p className="text-xs text-green-600">{result.pnl.gross_margin_pct}%</p>
                </div>
                <div className="bg-emerald-50 p-4 rounded-lg">
                  <p className="text-sm text-emerald-600">Utilidad Neta</p>
                  <p className="text-2xl font-bold text-emerald-800">
                    ${result.pnl.net_profit?.toLocaleString('es-MX', { maximumFractionDigits: 0 })}
                  </p>
                  <p className="text-xs text-emerald-600">{result.pnl.net_margin_pct}%</p>
                </div>
              </div>
            )}

            {/* Inventory Summary */}
            {result.summary && (
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-red-100 p-4 rounded-lg">
                  <p className="text-sm text-red-600">Crítico</p>
                  <p className="text-3xl font-bold text-red-800">{result.summary.critical}</p>
                </div>
                <div className="bg-orange-100 p-4 rounded-lg">
                  <p className="text-sm text-orange-600">Stock Bajo</p>
                  <p className="text-3xl font-bold text-orange-800">{result.summary.low}</p>
                </div>
                <div className="bg-yellow-100 p-4 rounded-lg">
                  <p className="text-sm text-yellow-600">Advertencia</p>
                  <p className="text-3xl font-bold text-yellow-800">{result.summary.warning}</p>
                </div>
                <div className="bg-green-100 p-4 rounded-lg">
                  <p className="text-sm text-green-600">Saludable</p>
                  <p className="text-3xl font-bold text-green-800">{result.summary.healthy}</p>
                </div>
              </div>
            )}

            {/* AI Response */}
            <div className="mt-4">
              <h4 className="font-semibold text-gray-700 mb-2">📊 Análisis AI</h4>
              {formatAIResponse(
                result.ai_forecast || 
                result.ai_analysis || 
                result.ai_schedule || 
                result.ai_suggestions ||
                result.ai_production_plan ||
                result.ai_routes ||
                result.response
              )}
            </div>

            {/* Generated At */}
            {result.generated_at && (
              <p className="text-xs text-gray-400 mt-4">
                Generado: {new Date(result.generated_at).toLocaleString('es-MX')}
              </p>
            )}
          </div>
        )}

        {/* Empty State */}
        {!loading && !result && !error && (
          <div className="text-center py-12 text-gray-400">
            <span className="text-6xl">
              {agents.find(a => a.id === activeAgent)?.icon}
            </span>
            <p className="mt-4">Haz clic en "Ejecutar" para iniciar el análisis</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAgentsTab;
